import { useEffect } from 'react';

import {
  CONNECT_ATTEMPT_KEY,
  CONNECT_CHANNEL,
  CONNECT_RESULT_TYPE,
  type ConnectResultMessage,
} from './connector-oauth';

/**
 * Handle OAuth connector callback params (`?connected=` / `?reauthorized=` / `?error=`).
 *
 * The popup lands on the connectors page after the backend redirect.
 * This hook sends the result to the opener window and closes the popup.
 * The redirect chain can cut `window.opener`, so the result also goes on a BroadcastChannel.
 */
export function useOAuthCallback() {
  useEffect(() => {
    const url = new URL(window.location.href);
    const connected = url.searchParams.get('connected');
    const reauthorized = url.searchParams.get('reauthorized');
    const error = url.searchParams.get('error');

    if (!connected && !reauthorized && !error) return;

    let attemptId: string | null = null;
    try {
      attemptId = sessionStorage.getItem(CONNECT_ATTEMPT_KEY);
      sessionStorage.removeItem(CONNECT_ATTEMPT_KEY);
    } catch {
      // Storage can be blocked or full. Continue without an attemptId.
    }

    if (window.opener || attemptId) {
      const result: ConnectResultMessage = {
        type: CONNECT_RESULT_TYPE,
        attemptId,
        connected: connected || undefined,
        reauthorized: reauthorized || undefined,
        error: error || undefined,
        errorDescription: url.searchParams.get('error_description') || undefined,
      };
      // The BroadcastChannel already delivers this when there's an attemptId.
      // Only fall back to postMessage when there is none to correlate.
      if (!attemptId) window.opener?.postMessage(result, window.location.origin);
      if (attemptId) {
        const channel = new BroadcastChannel(CONNECT_CHANNEL);
        channel.postMessage(result);
        channel.close();
      }
      window.close();
      if (window.closed) return;
    }

    // Fallback: not in a popup, or the popup cannot close itself.
    // Remove only connector params. Leave ?error= / ?error_description= so the auth UI can surface them.
    url.searchParams.delete('connected');
    url.searchParams.delete('reauthorized');
    window.history.replaceState({}, '', url.pathname + url.search);
  }, []);
}
